/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { BUILD_TARGET, HOST_URL } from 'config'
import { captureException } from 'utils/sentry'
import { axiosInstanceV3 } from "../components/mkt/v3/axiosInstanceV3";

export type CustomResponseFormat<T = any> = {
  code: number;
  debugMessage?: string;
  message?: string;
  status?: number;
  error?: string;
  data?: T;
  result?: T;
};

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<CustomResponseFormat>>;
  };

  getUri(config?: AxiosRequestConfig): string;

  request<T>(config: AxiosRequestConfig): Promise<T>;

  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (BUILD_TARGET === "dev") {
    console.info(
      "[",
      config.method?.toUpperCase(),
      config.url,
      "]",
      "\nparams: ",
      config.params
    );
  }
  return config;
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (BUILD_TARGET === "dev") {
    // console.info(
    //   `[${response.config.method?.toUpperCase()} ${
    //     response.config.url
    //   }]\ndata: ${JSON.stringify(response.data)}`
    // );
    // console.log(response.data);
  }
  return response.data;
};

const axiosInstancePromotion: CustomInstance = axios.create({
  withCredentials: true,
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
  headers: {
    // "X-Api-Key": API_KEY,
    // "X-Auth-Token": "",
    // "X-App-Id": "w:123",
  },
});

const onFulfilled = (response: AxiosResponse) => response;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

type TokenRefreshedListener = (accessToken: string) => void;
let tokenRefreshedListeners: TokenRefreshedListener[] = [];

export const addTokenRefreshedListener = (listener: TokenRefreshedListener) => {
  tokenRefreshedListeners.push(listener);
  return () => {
    tokenRefreshedListeners = tokenRefreshedListeners.filter(l => l !== listener);
  };
};

const notifyTokenRefreshed = (accessToken: string) => {
  tokenRefreshedListeners.forEach(listener => listener(accessToken));
};

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const onRejected = async (error: AxiosError<CustomResponseFormat>) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
    _retryCount?: number;
  };

  if (error.config) console.log(error);

  // 네트워크 에러 또는 타임아웃 처리
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      console.error("Request timeout:", error.message);
      captureException(error, {
        tags: { action: "axios_timeout" },
      });
    } else if (error.message === "Network Error") {
      console.error("Network error:", error.message);

      // 첫 Network Error는 warning으로 보고
      captureException(error, {
        tags: { action: "axios_network_error" },
        level: "warning",
      });

      // Network Error 시 자동 재시도 (최대 2회)
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < 2) {
        console.log(`Retrying request (attempt ${retryCount + 1}/2)...`);
        originalRequest._retryCount = retryCount + 1;

        // 재시도 전 짧은 대기
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));

        try {
          return await axiosInstancePromotion(originalRequest);
        } catch (retryError) {
          // 재시도도 실패한 경우
          if (originalRequest._retryCount >= 2) {
            console.error("All retry attempts failed. Reloading page...");

            // Sentry에 재시도 실패 보고
            captureException(retryError, {
              tags: { action: "axios_network_error_retry_failed" },
              extra: { retryCount: originalRequest._retryCount },
            });

            // 페이지 새로고침 (로그인 페이지가 아닌 경우에만)
            if (!window.location.pathname.includes("/promotion-login")) {
              window.location.href = window.location.href;
            }
          }
          throw retryError;
        }
      }
    }
  }

  if (error.response?.data) {
    const statusCode = error.response?.data.status;

    if (
      statusCode === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/user/v2/token/refresh")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
            }
            return axiosInstancePromotion(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.get(`${HOST_URL}/user/v2/token/refresh`, {
          withCredentials: true,
        });

        if (response.data.code === 0) {
          const newAccessToken = response.data.result.accessToken;

          if (newAccessToken) {
            setBearerTokenPromotion(newAccessToken);
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] =
                `Bearer ${newAccessToken}`;
            } else {
              originalRequest.headers = {
                Authorization: `Bearer ${newAccessToken}`,
              } as AxiosRequestHeaders;
            }
          }

          processQueue(null, newAccessToken);
          isRefreshing = false;

          return axiosInstancePromotion(originalRequest);
        } else {
          throw new Error("Token refresh failed");
        }
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;
        console.log(`401 ${JSON.stringify(error.config)}`);

        // 이미 로그인 페이지에 있으면 리다이렉트하지 않음 (무한루프 방지)
        if (!window.location.pathname.includes("/promotion-login")) {
          window.location.href = `${window.location.origin}/promotion-login`;
        }
        return Promise.reject(refreshError);
      }
    } else if (statusCode === 403) {
      console.log(`403 ${JSON.stringify(error.config)}`);

      // 이미 로그인 페이지에 있으면 리다이렉트하지 않음 (무한루프 방지)
      if (!window.location.pathname.includes("/promotion-login")) {
        window.location.href = `${window.location.origin}/promotion-login`;
      }
    }
  }

  return Promise.reject(error);
};

axiosInstancePromotion.interceptors.request.use(onRequest);
axiosInstancePromotion.interceptors.response.use(onResponse, onRejected);

export const setBearerTokenPromotion = (accessToken: string) => {
  if (axiosInstancePromotion.defaults?.headers) {
    // @ts-ignore
    axiosInstancePromotion.defaults.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  notifyTokenRefreshed(accessToken);
};

export { axiosInstancePromotion };
